import merge from 'lodash/merge.js'
import { type TDomains, type BaseJsonConfiguration, type TRef } from './types.js'

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Cypress: any
  }
}

type BuildDomainUtilsProps<TSupportedDomains extends TDomains> = {
  supportedDomains: TSupportedDomains
  defaultDomain: TSupportedDomains[number]
  domainOverride?: string
  frontendDomain: string
}

type BuildConfigForDomainProps<
  TSupportedDomains extends TDomains,
  TAppConfiguration,
  TJsonConfig extends BaseJsonConfiguration<TSupportedDomains, TAppConfiguration>,
> = {
  config: TJsonConfig
  domain: keyof TJsonConfig
}

export function buildDomainUtils<TSupportedDomains extends TDomains>({
  supportedDomains,
  defaultDomain,
  domainOverride,
  frontendDomain,
}: BuildDomainUtilsProps<TSupportedDomains>): {
  getDomain: (hostname?: string) => TSupportedDomains[number]
  getTopLevelDomain: () => string
  forDomain: <T>(values: Record<TSupportedDomains[number], T>, domain?: TSupportedDomains[number]) => T
  forTopLevelDomain: <T>(values: Record<string, T>) => T
  buildConfigForDomain: <
    TAppConfiguration,
    TJsonConfig extends BaseJsonConfiguration<TSupportedDomains, TAppConfiguration>,
  >({
    config,
    domain,
  }: BuildConfigForDomainProps<TSupportedDomains, TAppConfiguration, TJsonConfig>) => TAppConfiguration
} {
  const domainOverrideKey = 'domainOverride'
  // eslint-disable-next-line n/no-unsupported-features/node-builtins -- this is a browser environment
  const overrideDomain = (window?.sessionStorage?.getItem(domainOverrideKey) || domainOverride)?.toLowerCase()
  const supportedTlds = supportedDomains.map((domain) => domain.split('.').slice(-1)[0])

  if (overrideDomain || window.Cypress) {
    document.cookie = `X-FRONTEND-HOST=${overrideDomain || frontendDomain}; path=/;` // Forward to our backends, for development and testing purposes
  }

  //
  // Determine domain
  //
  function getDomain(hostname: string = window.location.hostname): TSupportedDomains[number] {
    if (overrideDomain) {
      return overrideDomain as TSupportedDomains[number]
    }

    const previewDomain = 'test.cl1.intra.dhlparcel.io'

    const host = hostname.toLowerCase()
    const domain: string | undefined = host.endsWith(previewDomain) // For example: preview-1-my-nl.cl1.intra.dhlparcel.io
      ? host.replace(`.${previewDomain}`, '').split('-').at(-1) // TODO: fix when previews are properly set up (previews are currently not working). Probably need to put the domain name in the preview domain, e.g. preview-1-my-dhlecommerce-nl.cl1.intra.dhlparcel.io
      : host.split('.').slice(-2).join('.')

    return supportedDomains.find((supportedDomain) => domain === supportedDomain) ?? defaultDomain
  }

  function getTopLevelDomain(): string {
    const domain = getDomain()
    return domain.split('.').slice(-1)[0]
  }

  //
  // Get a value based on the domain
  //
  function forDomain<T>(values: Record<TSupportedDomains[number], T>, domain?: TSupportedDomains[number]): T {
    const domainToUse = domain || getDomain()
    return values[domainToUse] ?? values[defaultDomain]
  }

  function forTopLevelDomain<T>(values: Record<string, T>): T {
    if (Object.keys(values).some((tld) => !supportedTlds.includes(tld))) {
      throw new Error(
        `Unsupported top-level domain provided. Supported top-level domains are: ${supportedTlds.join(', ')}`
      )
    }
    const tld = getTopLevelDomain()
    return values[tld]
  }

  function buildConfigForDomain<
    TAppConfiguration,
    TJsonConfig extends BaseJsonConfiguration<TSupportedDomains, TAppConfiguration>,
  >({
    config,
    domain,
  }: BuildConfigForDomainProps<TSupportedDomains, TAppConfiguration, TJsonConfig>): TAppConfiguration {
    const defaultConfig = config.default
    let configForDomain = config[domain]
    const refDomain = (configForDomain as unknown as TRef).$ref
    if (refDomain && typeof refDomain === 'string') {
      if (refDomain === domain) {
        throw new Error(`Configuration error: domain '${String(domain)}' points to itself via $ref`)
      }
      if (!config[refDomain as keyof TJsonConfig]) {
        throw new Error(
          `Configuration error: domain '${String(domain)}' points to unknown domain '${String(refDomain)}' via $ref`
        )
      }
      if (!supportedDomains.includes(refDomain)) {
        throw new Error(
          `Configuration error: domain '${String(domain)}' points to unsupported domain '${String(refDomain)}' via $ref`
        )
      }
      configForDomain = config[refDomain as keyof TJsonConfig]
    }
    const resultConfig = merge({}, defaultConfig, configForDomain)
    return resultConfig
  }

  return {
    getDomain,
    getTopLevelDomain,
    forDomain,
    forTopLevelDomain,
    buildConfigForDomain,
  }
}
